import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import CloseOutlinedIcon from "@mui/icons-material/CloseOutlined";
import {
  AUTH_SUCCESS_POPUP_COPY,
  AUTH_SUCCESS_POPUP_EVENT,
  consumeQueuedAuthSuccessPopup,
} from "utils/authSuccessPopup";
import "styles/auth-success-popup.css";

const AuthSuccessPopup = () => {
  const [kind, setKind] = useState(null);
  const popupRef = useRef(null);
  const copy = useMemo(
    () => (kind ? AUTH_SUCCESS_POPUP_COPY[kind] : null),
    [kind]
  );

  const show = useCallback((nextKind) => {
    if (!AUTH_SUCCESS_POPUP_COPY[nextKind]) return;
    setKind(nextKind);
  }, []);

  useEffect(() => {
    const queuedKind = consumeQueuedAuthSuccessPopup();
    if (queuedKind) show(queuedKind);
  }, [show]);

  useEffect(() => {
    const onAuthSuccess = (event) => {
      const nextKind = event.detail?.kind;
      if (AUTH_SUCCESS_POPUP_COPY[nextKind]) {
        consumeQueuedAuthSuccessPopup();
        show(nextKind);
      }
    };

    window.addEventListener(AUTH_SUCCESS_POPUP_EVENT, onAuthSuccess);
    return () => window.removeEventListener(AUTH_SUCCESS_POPUP_EVENT, onAuthSuccess);
  }, [show]);

  useEffect(() => {
    if (!kind) return undefined;

    const timeoutId = window.setTimeout(() => {
      setKind(null);
    }, 5000);

    return () => window.clearTimeout(timeoutId);
  }, [kind]);

  useEffect(() => {
    if (!kind) return undefined;
    const onPointerDown = (event) => {
      if (!popupRef.current?.contains(event.target)) setKind(null);
    };
    const onKeyDown = (event) => {
      if (event.key === "Escape") setKind(null);
    };
    document.addEventListener("pointerdown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("pointerdown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [kind]);

  if (!copy) return null;

  return (
    <div className="auth-success-popup" role="status" aria-live="polite">
      <div
        ref={popupRef}
        className="auth-success-popup__dialog"
        onClick={() => setKind(null)}
      >
        <button
          type="button"
          className="auth-success-popup__close"
          aria-label="Hinweis schließen"
          onClick={() => setKind(null)}
        >
          <CloseOutlinedIcon aria-hidden="true" />
        </button>
        <span className="auth-success-popup__mark" aria-hidden="true">
          <CheckCircleOutlineIcon />
        </span>
        <p className="auth-success-popup__kicker">{copy.kicker}</p>
        <h2 id="auth-success-popup-title">{copy.title}</h2>
        <p className="auth-success-popup__message">{copy.message}</p>
      </div>
    </div>
  );
};

export default AuthSuccessPopup;
